import {ConnectedPosition, OverlayModule} from '@angular/cdk/overlay';
import {ChangeDetectionStrategy, Component, ElementRef, computed, forwardRef, input, linkedSignal, output, signal, viewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {MatSymbol} from '../../models/mat-symbol';
import {OVERLAY_POSITIONS} from '../../overlay/overlay.constants';
import {OverlayPanelDirective} from '../../overlay/overlay-panel.directive';
import {SelectOption} from './select-dropdown';

/**
 * Single-select dropdown with the period-selector9 pill trigger: a compact pill showing the current
 * label, opening a CDK-overlay menu of options with a check on the selected one. Picking an option
 * never clears the value, so with `deselectable` off the selection is always one of the options;
 * with it on, clicking the option that is already checked toggles it back off to null.
 *
 * Usable two ways: `[value]` + `(valueChange)` for a plain binding, or `formControlName` /
 * `ngModel`, since it is also a `ControlValueAccessor`. The two do not conflict — a form writes
 * through {@link writeValue}, which feeds the same signal `[value]` seeds.
 */
@Component({
  selector: 'app-select-dropdown',
  imports: [OverlayModule, OverlayPanelDirective],
  templateUrl: './select-dropdown.component.html',
  styleUrl: './select-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: 'app-select-dropdown'},
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => SelectDropdownComponent),
  }],
})
export class SelectDropdownComponent<T> implements ControlValueAccessor {
  readonly options = input.required<SelectOption<T>[]>();
  /** The current selection. Seeds {@link selected}; a form control writes to the same signal. */
  readonly value = input<T | null>(null);
  /**
   * How an option is matched against the current value. Defaults to identity, which is wrong for
   * object values a form round-trips — the control can hold an equal-but-distinct instance, and
   * the trigger would then fall back to the placeholder. Pass an id comparison for those.
   */
  readonly compareWith = input<(a: T | null, b: T | null) => boolean>((a, b) => a === b);
  /** Shown on the trigger while nothing is selected. */
  readonly placeholder = input($localize`Select…`);
  /** Optional leading material symbol (glyph + colour + fill). */
  readonly matSymbol = input<MatSymbol | null>(null);
  /** Greys the trigger and blocks opening the menu. */
  readonly disabled = input(false);
  /**
   * Reduce the trigger to its {@link matSymbol}: no label, no caret, a square button. For a
   * dropdown whose current value is not worth naming on screen — the language switch, where the
   * globe is the affordance and the check in the menu is what reports the selection.
   */
  readonly iconOnly = input(false);
  /**
   * Let a click on the checked option toggle it off, leaving no selection. Off by default: most of
   * these dropdowns model a choice that is always made, where a click on the current option should
   * be a no-op rather than a way to end up with nothing.
   */
  readonly deselectable = input(false);
  /** Overlay positions for the menu; below-first, flipping above, by default. */
  readonly positions = input<ConnectedPosition[]>(OVERLAY_POSITIONS);

  readonly valueChange = output<T>();
  /** Emitted when a click deselects the checked option. A form control gets the null via the CVA. */
  readonly cleared = output<void>();

  protected readonly open = signal(false);

  /** The live selection: the `[value]` input until a pick or a form write moves it. */
  private readonly selected = linkedSignal<T | null>(() => this.value());
  /** Set by `setDisabledState`; ORed with the `disabled` input so either can block the menu. */
  private readonly formDisabled = signal(false);

  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());

  /** The selected option's label, or `null` when nothing matches (falls back to the placeholder). */
  protected readonly selectedLabel = computed(() => {
    const match = this.compareWith();
    return this.options().find(option => match(option.value, this.selected()))?.label ?? null;
  });

  protected isChecked(option: SelectOption<T>): boolean {
    return this.compareWith()(option.value, this.selected());
  }


  private onChange: (value: T | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: T | null): void {
    this.selected.set(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  protected toggle(): void {
    if (this.isDisabled()) {
      return;
    }
    if (this.open()) {
      this.onTouched();
    }
    this.open.update(open => !open);
  }

  protected select(option: SelectOption<T>): void {
    this.open.set(false);
    this.onTouched();
    if (this.isChecked(option)) {
      // Clicking the current option is a no-op unless the consumer allows no selection at all.
      if (this.deselectable()) {
        this.selected.set(null);
        this.onChange(null);
        this.cleared.emit();
      }
      return;
    }
    this.selected.set(option.value);
    this.onChange(option.value);
    this.valueChange.emit(option.value);
  }
}
